import { useTranslation } from 'react-i18next';
import Header from '../../templates/Header/Header';
import { useNavigateWithParams } from '../../../hooks/useNavigateWithParams';
import { useLocation } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import Title from '../../atoms/Title/Title';
import SUCCESS from "../../../assets/images/SUCCESS-HANDS.svg";
import LogoSvg from '../../atoms/LogoSvg/LogoSvg';
import Footer from '../../templates/Footer/Footer';
import StyledButton from '../../atoms/StyledButton/StyledButton';
import { useAcceptRetentionOfferMutation, useEditSubscriptionMutation } from '../../../rtk/currentSubscriptionsOptions/currentSubscriptionsOptions';
import { RetentionOffer } from '../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions';
import { AppRoutes } from '../../../routes/AppRoutes';
import WeenectCard from '../../atoms/Card/Card';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Text from '../../atoms/Text/Text';
import { openExternalUrl } from '../../../helpers/openExternalUrl';

export function TransferTrackerOffer() {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const [manageSubscriptionReason, { isLoading: isLoadingCancel }] = useEditSubscriptionMutation();
  const [acceptRetentionOffer, { isLoading: isLoadingAccept }] = useAcceptRetentionOfferMutation();
  const isLoading = isLoadingCancel || isLoadingAccept;

  const transferOffer: RetentionOffer | undefined = location.state?.transferOffer;
  const transferRequestUrl = transferOffer?.transfer_request_url;
  const otherOffers: RetentionOffer[] = location.state?.offers ?? [];
  const cancelData: { cancel_reason: string; cancel_explanation?: string } = location.state?.cancelData;

  async function acceptTransfer() {
    if (!transferOffer || !transferRequestUrl) return;
    // Opened before any await, otherwise the popup is blocked
    const opened = openExternalUrl(transferRequestUrl);

    try {
      // Stops the renewal like a standard cancellation, and records the transfer on the back-end side
      await acceptRetentionOffer({
        retention_offer_id: transferOffer.id,
        cancel_explanation: cancelData?.cancel_explanation,
      }).unwrap();
    } catch {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
      return;
    }

    // Replaces this screen so the user lands on the subscriptions screen when coming back
    navigate(AppRoutes.MANAGE_SUBSCRIPTIONS, { replace: true });
    // Mobile app or popup blocked: open the form in the current view, once the renewal is stopped
    if (!opened) window.location.assign(transferRequestUrl);
  }

  // Continues the standard cancellation tunnel: remaining retention offers, or cancellation
  async function refuseTransfer() {
    if (otherOffers.length) {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_RETENTION_OFFERS, { state: location.state });
      return;
    }

    try {
      await manageSubscriptionReason({ renewal_active: false, ...cancelData }).unwrap();
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'cancellation_success', type: 'subscription' } });
    } catch {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
    }
  }

  return (
    <>
      <Header
        headerStep={{ context: "manage" }}
        title={t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.HEADER")}
        goBack={() => navigate(-1)}
      />
      <Stack sx={{ height: '100%', padding: '1rem', alignItems: 'center', justifyContent: 'space-evenly' }}>
        <LogoSvg className="Logos" src={SUCCESS} />
        <Title variant='h1' align='center'>
          { t('cancellation:no_use_transfer_title') }
        </Title>
        <Typography align='center' sx={{ fontSize: 18 }}>
          { t('cancellation:no_use_transfer_subtitle') }
        </Typography>
        <WeenectCard>
          <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <InfoOutlinedIcon className='transfer-info-icon' />
            <Text>
              { t('cancellation:no_use_transfer_info') }
            </Text>
          </Stack>
        </WeenectCard>
      </Stack>
      <Footer headerStep={{ context: "manage" }}>
        <>
          <StyledButton
            children={t("cancellation:no_use_transfer_cta_primary")}
            onClick={acceptTransfer}
            disabled={isLoading || !transferRequestUrl}
          />
          <StyledButton
            children={t("cancellation:no_use_transfer_cta_secondary")}
            onClick={refuseTransfer}
            disabled={isLoading}
            className='white'
          />
        </>
      </Footer>
    </>
  )
}